import { toast } from 'react-toastify';

import extractEventId from '../utils/helpers';
import { requestComplete, requestError, requestStarted } from '../../core/actions/http-request';
import { closeModal, openModal } from '../../core/actions/modal';
import {
    doPublishTickets, doSaveBankAccount, doSaveProvider, doSaveSellPriceTickets, doUnselectSellTicket,
    doValidateTicketCodes, fetchBankAccount, fetchEvents, fetchTickets,
} from '../api/sell';
import {
    decoratePayload, decoratePayloadSellPriceTickets, populateFields, populateFormErrors, populateFormTickets,
} from '../api/decorator';
import {
    changeFormByIndex, handleRequestErrors, setFieldErrorMessage, setFieldVisibility, setHasMultipleEvents,
} from './forms';
import { DONE_MODAL, LOGIN_MODAL_NAME, OTHER_PROVIDER_MODAL_NAME } from '../constants/modal';
import {
    BANK_ACCOUNT_FORM, BANK_ACCOUNT_BIC_FIELD, SALE_OPTIONS_FORM, TICKET_CODES_FORM, BANK_ACCOUNT_NAME_FIELD,
} from '../reducers/features';

export const SET_LOGIN_MODAL_STEP = 'SET_LOGIN_MODAL_STEP';
export const setLoginModalStep = (step) => ({
    type: SET_LOGIN_MODAL_STEP,
    payload: step,
});

export const SET_SELL_EVENTS = 'SET_SELL_EVENTS';
export const setSellEvents = (events) => ({
    type: SET_SELL_EVENTS,
    payload: events,
});

export const SELECT_ALL_TICKETS = 'SELECT_ALL_TICKETS';
export const selectAllTickets = () => ({
    type: SELECT_ALL_TICKETS,
});

export const DESELECT_ALL_TICKETS = 'DESELECT_ALL_TICKETS';
export const deselectAllTickets = () => ({
    type: DESELECT_ALL_TICKETS,
});

export const SET_SELL_TICKETS = 'SET_SELL_TICKETS';
export const setSellTickets = (payload) => ({
    type: SET_SELL_TICKETS,
    payload,
});
export const TOGGLE_TICKET_SELECTION = 'TOGGLE_TICKET_SELECTION';
export const toggleTicketSelection = (code) => ({
    type: TOGGLE_TICKET_SELECTION,
    payload: code,
});

export const getEvents = () => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_GET_TICKETS_REQUEST_NAME));
        const response = await fetchEvents();
        const formattedResponse = await response.json();
        dispatch(setSellEvents(formattedResponse.data));
    } catch (e) {
        if (e.status === 401) {
            dispatch(openModal(LOGIN_MODAL_NAME));
        } else {
            dispatch(requestError(SELL_GET_TICKETS_REQUEST_NAME));
        }
    } finally {
        dispatch(requestComplete(SELL_GET_TICKETS_REQUEST_NAME));
    }
};
export const SELL_GET_TICKETS_REQUEST_NAME = 'SELL_GET_TICKETS_REQUEST_NAME';
export const getTickets = (step, eventId) => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_GET_TICKETS_REQUEST_NAME));
        const response = await fetchTickets(step, eventId);
        const formattedResponse = await response.json();
        dispatch(setSellTickets(formattedResponse.data));
        populateFormTickets(formattedResponse.data, dispatch);
    } catch (e) {
        dispatch(requestError(SELL_GET_TICKETS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(SELL_GET_TICKETS_REQUEST_NAME));
    }
};

export const SELL_SAVE_TICKETS_REQUEST_NAME = 'SELL_SAVE_TICKETS_REQUEST_NAME';

export const unselectTicket = (code, errorNotify) => async (dispatch) => {
    try {
        const response = await doUnselectSellTicket(extractEventId(), code);
        const formattedResponse = await response.json();
        if (formattedResponse.status === 'success') {
            dispatch(toggleTicketSelection(code));
            return;
        }
        errorNotify();
    } catch (e) {
        errorNotify();
    }
};
export const saveSellPriceTickets = (tickets, goToNextStep) => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_SAVE_TICKETS_REQUEST_NAME));
        const payload = decoratePayloadSellPriceTickets(tickets);
        const response = await doSaveSellPriceTickets(extractEventId(), payload);
        const formattedResponse = await response.json();
        if (formattedResponse.status === 'success') {
            goToNextStep();
            return;
        }
        populateFormErrors(formattedResponse, dispatch);
    } catch (e) {
        dispatch(requestError(SELL_SAVE_TICKETS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(SELL_SAVE_TICKETS_REQUEST_NAME));
    }
};

export const getBankAccount = () => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_GET_TICKETS_REQUEST_NAME));
        const response = await fetchBankAccount();
        const formattedResponse = await response.json();
        if (formattedResponse.data && formattedResponse.data.isBicRequired) {
            dispatch(setFieldVisibility(BANK_ACCOUNT_FORM, BANK_ACCOUNT_BIC_FIELD, true));
        }
        populateFields(BANK_ACCOUNT_FORM, formattedResponse.data, dispatch);
    } catch (e) {
        dispatch(requestError(SELL_GET_TICKETS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(SELL_GET_TICKETS_REQUEST_NAME));
    }
};

export const saveBankAccount = (inputs, goToNextStep, intl) => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_SAVE_TICKETS_REQUEST_NAME));
        const payload = decoratePayload(inputs);
        if (!hasAtLeastTwoWords(payload.accountName)) {
            dispatch(setFieldErrorMessage(BANK_ACCOUNT_FORM, BANK_ACCOUNT_NAME_FIELD, intl.formatMessage({ id: 'cart_full_name_error' })));
            return;
        }

        const response = await doSaveBankAccount(payload);
        const formattedResponse = await response.json();
        if (formattedResponse.status === 'error') {
            const isBicRequired = formattedResponse.errors.some((error) => error.field === 'bic');
            if (isBicRequired) {
                const bicErrorMessage = formattedResponse.errors.filter((error) => error.field === 'bic');
                dispatch(setFieldVisibility(BANK_ACCOUNT_FORM, BANK_ACCOUNT_BIC_FIELD, true));
                dispatch(setFieldErrorMessage(
                    BANK_ACCOUNT_FORM,
                    BANK_ACCOUNT_BIC_FIELD,
                    bicErrorMessage.length > 0 && bicErrorMessage[0].message,
                ));
                formattedResponse.errors = formattedResponse.errors.filter((error) => !(error.field === 'bic'));
            }
            handleRequestErrors(BANK_ACCOUNT_FORM, formattedResponse, dispatch);
            return;
        }
        goToNextStep();
    } catch (e) {
        dispatch(requestError(SELL_SAVE_TICKETS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(SELL_SAVE_TICKETS_REQUEST_NAME));
    }
};

const hasAtLeastTwoWords = (str) => str.trim().split(/\s+/).filter(Boolean).length >= 2;

export const publishTickets = (inputs) => async (dispatch) => {
    try {
        dispatch(requestStarted(SELL_GET_TICKETS_REQUEST_NAME));
        const payload = decoratePayload(inputs);
        const response = await doPublishTickets(extractEventId(), payload);
        const formattedResponse = await response.json();
        if (formattedResponse.status === 'error') {
            handleRequestErrors(SALE_OPTIONS_FORM, formattedResponse, dispatch);
            return;
        }
        dispatch(openModal(DONE_MODAL));
    } catch (e) {
        dispatch(requestError(SELL_GET_TICKETS_REQUEST_NAME));
    } finally {
        dispatch(requestComplete(SELL_GET_TICKETS_REQUEST_NAME));
    }
};

export const validateTicketCodes = (codes, errorNotify) => async (dispatch) => {
    try {
        const response = await doValidateTicketCodes({ codes });
        const formattedResponse = await response.json();
        if (formattedResponse.status === 'success') {
            dispatch(setHasMultipleEvents(formattedResponse.data.hasMultipleEvents));
            formattedResponse.data.codes.forEach((item, index) => {
                dispatch(changeFormByIndex(TICKET_CODES_FORM, index, item));
            });
            return;
        }
        errorNotify();
    } catch (e) {
        errorNotify();
    }
};

export const saveProvider = (email, provider) => async (dispatch) => {
    const response = await doSaveProvider({ email, provider });
    const formattedResponse = await response.json();
    if (formattedResponse.status === 'error') {
        toast.error(formattedResponse.message);
        return;
    }
    toast.success(formattedResponse.message);
    dispatch(closeModal(OTHER_PROVIDER_MODAL_NAME));
};
